/**
 * Complete Activity Modal
 *
 * Modal for marking an activity as completed with outcome
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, ThumbsUp, Minus, ThumbsDown, UserX } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import type { CrmActivityDetail, CrmActivityOutcome } from '../../types';

interface CompleteActivityModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity: CrmActivityDetail | null;
  onComplete: (outcome: CrmActivityOutcome, notes?: string) => Promise<void>;
  isLoading?: boolean;
}

const OUTCOME_OPTIONS: Array<{
  value: CrmActivityOutcome;
  labelKey: string;
  descKey: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
}> = [
  {
    value: 'positive',
    labelKey: 'activities.outcome.positive',
    descKey: 'activities.outcome.positiveDesc',
    icon: ThumbsUp,
    color: 'border-green-500 bg-green-50 text-green-700',
  },
  {
    value: 'neutral',
    labelKey: 'activities.outcome.neutral',
    descKey: 'activities.outcome.neutralDesc',
    icon: Minus,
    color: 'border-gray-500 bg-gray-50 text-gray-700',
  },
  {
    value: 'negative',
    labelKey: 'activities.outcome.negative',
    descKey: 'activities.outcome.negativeDesc',
    icon: ThumbsDown,
    color: 'border-red-500 bg-red-50 text-red-700',
  },
  {
    value: 'no_show',
    labelKey: 'activities.outcome.no_show',
    descKey: 'activities.outcome.no_showDesc',
    icon: UserX,
    color: 'border-amber-500 bg-amber-50 text-amber-700',
  },
];

export function CompleteActivityModal({
  open,
  onOpenChange,
  activity,
  onComplete,
  isLoading,
}: CompleteActivityModalProps) {
  const { t } = useTranslation('crm');
  const [selectedOutcome, setSelectedOutcome] = useState<CrmActivityOutcome>(null);
  const [notes, setNotes] = useState('');

  const handleComplete = async () => {
    await onComplete(selectedOutcome, notes || undefined);
    // Reset form
    setSelectedOutcome(null);
    setNotes('');
    onOpenChange(false);
  };

  const handleClose = () => {
    setSelectedOutcome(null);
    setNotes('');
    onOpenChange(false);
  };

  if (!activity) return null;

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!newOpen) { setSelectedOutcome(null); setNotes(''); }
      onOpenChange(newOpen);
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-green-600" />
            {t('activities.completeModal.title')}
          </DialogTitle>
          <DialogDescription>
            {t('activities.completeModal.description', { title: activity.title })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Outcome Selection */}
          <div className="space-y-2">
            <Label>{t('activities.completeModal.outcomeLabel')}</Label>
            <div className="grid grid-cols-2 gap-2">
              {OUTCOME_OPTIONS.map((option) => {
                const Icon = option.icon;
                const isSelected = selectedOutcome === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setSelectedOutcome(option.value)}
                    className={cn(
                      'flex items-center gap-2 p-3 rounded-lg border-2 text-left transition-colors',
                      isSelected ? option.color : 'border-gray-200 hover:border-gray-300'
                    )}
                  >
                    <Icon className="h-4 w-4 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-medium">{t(option.labelKey)}</p>
                      <p className="text-xs text-gray-500">{t(option.descKey)}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="outcomeNotes">{t('activities.completeModal.notesLabel')}</Label>
            <Textarea
              id="outcomeNotes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('activities.completeModal.notesPlaceholder')}
              rows={3}
              disabled={isLoading}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose} disabled={isLoading}>
            {t('buttons.cancel')}
          </Button>
          <Button onClick={handleComplete} disabled={isLoading}>
            {isLoading ? t('activities.completeModal.completing') : t('activities.completeModal.completeActivity')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
